// ─── colors.js ────────────────────────────────────────────────────────────────
// Single source of truth for every color in the application.
// Import this anywhere — theme, components, utilities, tests.
// ─────────────────────────────────────────────────────────────────────────────

export const palette = {
  thryv: {
    bg: '#f4f6fb',
    bgAlt: '#dfe5f7',
    borderAlt: '#CEDCF9',
    gray: '#ECEEF1',
    blue: '#3D5199',
    orange: '#FF5000',

    plum: {
      // chart labels this dark stop "Night", base/light stops "Plum"
      dark: '#601947',
      base: '#80225F',
      light1: '#D86DB3',
      light2: '#F2CEE5',
    },
    fushia: {
      dark: '#AB268E',
      base: '#CC2DAA',
      light1: '#D86DB3', // ⚠ identical to plum.light1
      light2: '#FFEEFA',
    },
    ruby: {
      dark: '#D60047',
      base: '#FF0054',
      light1: '#FFA3C1',
      light2: '#FFF5F8',
    },
    orange: {
      // primary brand accent — filled star in source chart
      dark: '#D63F00',
      base: '#FF5000',
      light1: '#FF5000', // ⚠ identical to base
      light2: '#FF5000', // ⚠ identical to base
    },
    topaz: {
      dark: '#EF5900',
      base: '#FF8034',
      light1: '#FEDAA0',
      light2: '#FFF2DD',
    },
    amber: {
      dark: '#D63F00', // ⚠ identical to orange.dark
      base: '#FFDD00',
      light1: '#FEDAA0', // ⚠ identical to topaz.light1
      light2: '#FFF2CC',
    },
    teal: {
      dark: '#009075',
      base: '#00E2B7',
      light1: '#5DFFE0',
      light2: '#D8FFF8',
    },
    green: {
      dark: '#006450',
      base: '#008D71',
      light1: '#00DFB2',
      light2: '#ACFFEE',
    },
    jade: {
      dark: '#006450', // ⚠ identical to green.dark
      base: '#39AA9E',
      light1: '#91DAD2',
      light2: '#ACFFEE', // ⚠ identical to green.light2
    },
    sapphire: {
      // primary brand blue — filled star in source chart
      dark: '#006450', // ⚠ looks wrong: teal-green hex on a swatch rendered dark navy/blue
      base: '#5378FC',
      light1: '#96ACFD',
      light2: '#F4F6FB',
    },
    cornflower: {
      // filled star in source chart
      dark: '#26325F',
      base: '#5378FC', // ⚠ identical to sapphire.base
      light1: '#96ACFD', // ⚠ identical to sapphire.light1
      light2: '#F4F6FB', // ⚠ identical to sapphire.light2
    },
    midnight: {
      // filled star in source chart
      dark: '#1D2541',
      base: '#29355D',
      light1: '#586EB6',
      light2: '#BDC6E5',
    },
  },
  // ── Neutrals ──────────────────────────────────────────────────────────────
  neutral: {
    0: '#ffffff',
    150: '#EAEAEA',
    50: '#f9f9f9',
    100: '#f0f0f0',
    150: '#EAEAEA',
    200: '#e0e0e0',
    300: '#bbbbbb',
    400: '#aaaaaa',
    450: '#808080',
    500: '#888888',
    600: '#666666',
    700: '#444444',
    800: '#333333',
    900: '#111111',
    1000: '#000000',
  },

  // ── Brand / Primary ───────────────────────────────────────────────────────
  brand: {
    50: '#dbeafe',
    100: '#bfdbfe',
    250: '#A5B7F6',
    200: '#93c5fd',
    300: '#60a5fa',
    400: '#3b82f6',
    500: '#0070f3', // ← primary action color
    600: '#0051cc',
    650: '#0049D4',
    700: '#003d99',
    800: '#002a66',
    900: '#001833',
  },

  // ── Success ───────────────────────────────────────────────────────────────
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#166534',
    800: '#14532d',
    900: '#052e16',
  },

  // ── Warning ───────────────────────────────────────────────────────────────
  warning: {
    15: '#FFF9F6',
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#451a03',
  },

  // ── Danger ────────────────────────────────────────────────────────────────
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  // ── Info ──────────────────────────────────────────────────────────────────
  info: {
    15: '#F1F5FF',
    25: '#E1EAFF',
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },

  blueGray: {
    100: '#DDE1EA',
    200: '#C5CCd8',
    300: '#ACB5C6',
  },
}

// ─── Semantic aliases ─────────────────────────────────────────────────────────
// Map meaning → palette stop. Change one line to retheme the whole app.

export const colors = {
  // Backgrounds
  bg: {
    primary: palette.neutral[0],
    secondary: palette.neutral[50],
    tertiary: palette.neutral[100],
    inverse: palette.neutral[1000],
    brand: palette.thryv.bg,
    brandAlt: palette.thryv.bgAlt,
    tGray: palette.thryv.gray,
    disabled: '#e8e8e8',
    blueSubtleBg: palette.info[15],
    infoLight: palette.info[25],
    warningLight: palette.warning[15],
  },

  // Text
  text: {
    primary: palette.neutral[1000],
    secondary: palette.neutral[600],
    muted: palette.neutral[400],
    inverse: palette.neutral[0],
    link: palette.brand[500],
    linkStrong: palette.brand[650],
    linkHover: palette.brand[600],
    brand: palette.thryv.blue,
    subtle: palette.neutral[450],
  },

  // Borders
  border: {
    dark: palette.neutral[1000],
    light: palette.neutral[100],
    default: palette.neutral[200],
    strong: palette.neutral[300],
    focus: palette.brand[500],
    focusSubtle: palette.brand[250],
    focusStrong: palette.brand[650],
    alt: palette.thryv.borderAlt,
    brand: palette.thryv.orange.base,
    subtle: palette.neutral[150],
  },

  // Gradients
  gradient: {
    thryvSurface: `linear-gradient(${palette.neutral[0]}00, ${palette.blueGray[100]}BF)`,
  },

  // Actions (buttons, links)
  action: {
    primary: palette.brand[500],
    primaryHover: palette.brand[600],
    primaryText: palette.neutral[0],
    secondary: palette.neutral[900],
    secondaryHover: palette.neutral[800],
    secondaryText: palette.neutral[0],
  },

  // Status
  status: {
    success: {
      bg: palette.success[50],
      border: palette.success[200],
      text: palette.success[700],
      icon: palette.success[500],
    },
    warning: {
      bg: palette.warning[50],
      border: palette.warning[200],
      text: palette.warning[800],
      icon: palette.warning[500],
    },
    danger: {
      bg: palette.danger[50],
      border: palette.danger[200],
      text: palette.danger[800],
      icon: palette.danger[500],
    },
    info: {
      bg: palette.info[50],
      border: palette.info[200],
      text: palette.info[800],
      icon: palette.info[500],
    },
  },

  // Input fields
  input: {
    bg: palette.neutral[0],
    border: palette.neutral[300],
    borderHover: palette.neutral[1000],
    borderFocus: palette.brand[500],
    borderError: palette.danger[600],
    focusRing: palette.brand[100],
    errorRing: palette.danger[100],
    placeholder: palette.neutral[400],
    text: palette.neutral[1000],
  },

  gradient: {
    thryvSurface: `linear-gradient(${palette.neutral[0]}00, ${palette.blueGray[100]}BF)`,
  },
}
